import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import * as stakeholderApprovalService from '../services/stakeholderApprovalService';
import type { StakeholderApproval, ApprovalSummary } from '../services/stakeholderApprovalService';

interface StakeholderRow {
  userId: string;
  name: string;
  email: string;
  brdApproval: StakeholderApproval | null;
  timelineApproval: StakeholderApproval | null;
  fullyApproved: boolean;
}

export default function ProjectApprovals(): React.ReactElement {
  const { projectId } = useParams<{ projectId: string }>();
  const { token } = useAuth();
  const [summary, setSummary] = useState<ApprovalSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadApprovals() {
      if (!token || !projectId) return;
      try {
        setLoading(true);
        const data = await stakeholderApprovalService.getProjectApprovals(projectId, token);
        setSummary(data);
      } catch (err: any) {
        console.error('Error loading approvals:', err);
        setError(err.response?.data?.error || 'Failed to load project approvals');
      } finally {
        setLoading(false);
      }
    }
    loadApprovals();
  }, [token, projectId]);

  // Group approvals by stakeholder into rows
  const stakeholderRows: StakeholderRow[] = [];
  if (summary) {
    const grouped: Record<string, { name: string; email: string; approvals: StakeholderApproval[] }> = {};
    for (const approval of summary.approvals) {
      if (!grouped[approval.user_id]) {
        grouped[approval.user_id] = {
          name: `${approval.user_first_name || ''} ${approval.user_last_name || ''}`.trim(),
          email: approval.user_email || '',
          approvals: []
        };
      }
      grouped[approval.user_id].approvals.push(approval);
    }

    for (const [userId, group] of Object.entries(grouped)) {
      const brd = group.approvals.find(a => a.line_item_key === 'brd_approval') || null;
      const timeline = group.approvals.find(a => a.line_item_key === 'timeline_approval') || null;
      const fullyApproved = group.approvals.every(a => a.is_approved);
      stakeholderRows.push({
        userId,
        name: group.name || 'Unknown',
        email: group.email,
        brdApproval: brd,
        timelineApproval: timeline,
        fullyApproved
      });
    }
  }

  const totalStakeholders = summary?.total_stakeholders ?? 0;
  const fullyApproved = summary?.fully_approved_stakeholders ?? 0;
  const pending = totalStakeholders - fullyApproved;

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-2xl font-bold text-white">Project Approvals</h1>
          <p className="text-blue-100 mt-1">Track stakeholder approvals for BRD and timeline</p>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : stakeholderRows.length > 0 ? (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                    <svg className="w-6 h-6 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Total Stakeholders</p>
                    <p className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">{totalStakeholders}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                    <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Fully Approved</p>
                    <p className="text-3xl font-bold text-green-600 dark:text-green-400">{fullyApproved}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                    <svg className="w-6 h-6 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Pending</p>
                    <p className="text-3xl font-bold text-orange-600 dark:text-orange-400">{pending}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Stakeholder Table */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
              <div className="border-b border-gray-200 dark:border-gray-700 p-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Stakeholder Approval Status</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Stakeholder</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">BRD Approval</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Timeline Approval</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Overall Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {stakeholderRows.map((row) => (
                      <tr key={row.userId} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{row.name}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">{row.email}</div>
                        </td>
                        <td className="px-6 py-4">
                          {row.brdApproval ? (
                            row.brdApproval.is_approved ? (
                              <div>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                                  Approved
                                </span>
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  {row.brdApproval.approver_initials && <span className="font-medium">{row.brdApproval.approver_initials}</span>}
                                  {row.brdApproval.approved_at && <span> &middot; {formatDate(row.brdApproval.approved_at)}</span>}
                                </div>
                              </div>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                                Pending
                              </span>
                            )
                          ) : (
                            <span className="text-xs text-gray-400 dark:text-gray-500">N/A</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {row.timelineApproval ? (
                            row.timelineApproval.is_approved ? (
                              <div>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                                  Approved
                                </span>
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  {row.timelineApproval.approver_initials && <span className="font-medium">{row.timelineApproval.approver_initials}</span>}
                                  {row.timelineApproval.approved_at && <span> &middot; {formatDate(row.timelineApproval.approved_at)}</span>}
                                </div>
                              </div>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                                Pending
                              </span>
                            )
                          ) : (
                            <span className="text-xs text-gray-400 dark:text-gray-500">N/A</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {row.fullyApproved ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                              Fully Approved
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                              Pending
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          /* Empty State */
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">No Approvals Yet</h3>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Stakeholder approvals will appear here once stakeholders have been added to this project and the approval process has been initiated.
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}
